import { useMemo } from "react";

import { ListBox, type Selection } from "react-aria-components";

import { TextField } from "#components/v2/TextField";

import { CustomListItem } from "../CustomListItem";

import type { SearchColumn } from "./types";

export interface SearchColumnProps {
  column: SearchColumn;
  onSelect: (value: string) => void;
  selectedValue?: string;
  onSearch?: (searchTerm: string) => void;
  searchTerm?: string;
}

export function SearchColumn({
  column,
  onSelect,
  selectedValue,
  onSearch,
  searchTerm = "",
}: SearchColumnProps) {
  // Check if the selected value is a custom value (not in the items list)
  const isCustomValue = selectedValue && !column.items.some((item) => item.id === selectedValue);

  // Handle input value change
  const handleInputChange = (value: string) => {
    onSearch?.(value);
  };

  // Filter items based on search value
  const filteredItems = useMemo(() => {
    if (!searchTerm) {
      return column.items;
    }

    const lowerSearchTerm = searchTerm.toLowerCase();
    return column.items.filter((item) => item.name.toLowerCase().includes(lowerSearchTerm));
  }, [column.items, searchTerm]);

  const handleSelectionChange = (selection: Selection) => {
    if (selection !== "all" && selection.size > 0) {
      const selectedKey = Array.from(selection)[0];
      if (typeof selectedKey === "string") {
        onSelect(selectedKey);
        handleInputChange("");
      }
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      e.preventDefault();
      if (column.allowCustomValue) {
        const trimmedValue = searchTerm.trim();
        if (trimmedValue) {
          onSelect(trimmedValue);
          handleInputChange("");
        }
      }
    }
  };

  const trimmedSearchTerm = searchTerm.trim();
  const showCustomValueSuggestion =
    column.allowCustomValue &&
    trimmedSearchTerm &&
    !filteredItems.some((item) => item.name === trimmedSearchTerm) &&
    !filteredItems.some((item) => item.id === trimmedSearchTerm);

  return (
    <>
      <TextField
        aria-label={`${column.title} search`}
        className="w-full gap-0 px-2 py-1"
        inputProps={{
          placeholder: column.placeholder,
          onKeyDown: handleKeyPress,
          className: "h-[1.625rem] text-body-small",
        }}
        onChange={handleInputChange}
        value={searchTerm}
      />

      <ListBox
        aria-label={`${column.title} options`}
        onSelectionChange={handleSelectionChange}
        selectedKeys={selectedValue ? [selectedValue] : []}
        selectionMode="single"
      >
        {showCustomValueSuggestion ? (
          <CustomListItem
            id={`${trimmedSearchTerm}`}
            key={`custom-${trimmedSearchTerm}`}
            textValue={`Use "${trimmedSearchTerm}"`}
          >
            <div className="gap-0.5 flex flex-col">
              <div className="text-body-small-strong">Use &quot;{trimmedSearchTerm}&quot;</div>
              <div className="text-content-secondary text-label-extra-small">
                Please enter or click here to use this value
              </div>
            </div>
          </CustomListItem>
        ) : null}

        {isCustomValue && selectedValue && !searchTerm ? (
          <CustomListItem
            className="border-b-2 border-outline-brand italic"
            id={selectedValue}
            key={`selected-${selectedValue}`}
            textValue={selectedValue}
          >
            {selectedValue}
          </CustomListItem>
        ) : null}

        {filteredItems.map((item) => (
          <CustomListItem id={item.id} key={item.id} textValue={item.name}>
            {item.element || item.name}
          </CustomListItem>
        ))}
      </ListBox>
    </>
  );
}
